import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import storageService from '../services/storageService';
import { datosAPI, catalogosAPI } from '../utils/api';
import { FiSave, FiSend, FiArrowLeft, FiInfo } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function HistoricidadCrearPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [dato, setDato] = useState(null);
  const [tema, setTema] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [textoHistorico, setTextoHistorico] = useState('');
  const [palabrasClave, setPalabrasClave] = useState('');
  const [imagen, setImagen] = useState('');
  const [citaBiblica, setCitaBiblica] = useState('');
  const [strongHebreo, setStrongHebreo] = useState('');
  const [strongGriego, setStrongGriego] = useState('');
  const [referencias, setReferencias] = useState('');
  const [fechaEvento, setFechaEvento] = useState('');
  const [selectedCatalogos, setSelectedCatalogos] = useState([]);
  const [allCatalogos, setAllCatalogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLocalDato, setIsLocalDato] = useState(false);

  // Verificar permisos del usuario
  const canSubmitToBackend = user?.rol === 'administrador' || user?.modulePermissions?.some(
    p => p.moduleName === 'historicidad' && ['gestor', 'validador', 'editor', 'historiador'].includes(p.roleName)
  );

  useEffect(() => {
    const loadDato = async () => {
      if (id) {
        setLoading(true);
        try {
          if (id.startsWith('local_')) {
            setIsLocalDato(true);
            const localId = parseInt(id.replace('local_', ''));
            await storageService.init();
            const localDato = await storageService.historicalData.obtenerPorId(localId);
            if (localDato) {
              setDato(localDato);
              setTema(localDato.tema || '');
              setDescripcion(localDato.descripcion || '');
              setTextoHistorico(localDato.textoHistorico || '');
              setPalabrasClave(localDato.palabrasClave || '');
              setImagen(localDato.imagen || '');
              setCitaBiblica(localDato.citaBiblica || '');
              setStrongHebreo(localDato.strongHebreo || '');
              setStrongGriego(localDato.strongGriego || '');
              setReferencias(localDato.referencias || '');
              setFechaEvento(localDato.fechaEvento || '');
              setSelectedCatalogos(localDato.catalogos || []);
            }
          } else {
            setIsLocalDato(false);
            const backendDato = await datosAPI.getPorId(id);
            const datoData = backendDato.data.data;
            setDato(datoData);
            setTema(datoData.tema || '');
            setDescripcion(datoData.descripcion || '');
            setTextoHistorico(datoData.textoHistorico || '');
            setPalabrasClave(datoData.palabrasClave || '');
            setImagen(datoData.imagen || '');
            setCitaBiblica(datoData.citaBiblica || '');
            setStrongHebreo(datoData.strongHebreo || '');
            setStrongGriego(datoData.strongGriego || '');
            setReferencias(datoData.referencias || '');
            setFechaEvento(datoData.fechaEvento || '');
            setSelectedCatalogos(datoData.catalogos || []);
          }
        } catch (error) {
          console.error('Error cargando dato:', error);
          alert('Error al cargar el dato histórico.');
          navigate('/historicidad');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    const loadCatalogos = async () => {
      try {
        const response = await catalogosAPI.listar();
        setAllCatalogos(response.data.catalogos || response.data || []);
      } catch (error) {
        console.error('Error cargando catálogos:', error);
      }
    };

    loadDato();
    loadCatalogos();
  }, [id, navigate, user]);

  const handleSubmit = async (e, sendToReview = false) => {
    e.preventDefault();
    setLoading(true);

    if (!tema || !descripcion) {
      alert('Tema y descripción son obligatorios');
      setLoading(false);
      return;
    }

    const datoData = {
      tema,
      descripcion,
      textoHistorico,
      palabrasClave,
      imagen,
      citaBiblica,
      strongHebreo,
      strongGriego,
      referencias,
      fechaEvento,
      catalogos: selectedCatalogos.map(cat => typeof cat === 'object' ? cat.id : cat),
      usuarioInformanteId: user?.id
    };

    try {
      if (isLocalDato) {
        if (sendToReview) {
          // Enviar a revisión al backend
          await datosAPI.submitDato(datoData);
          await storageService.historicalData.eliminar(dato.id);
          alert('✅ Dato enviado a revisión exitosamente. Un gestor lo revisará pronto.');
          navigate('/historicidad');
        } else {
          // Guardar/actualizar localmente
          if (dato) {
            await storageService.historicalData.actualizar(dato.id, {
              ...datoData,
              estado: 'borrador_local',
              updatedAt: new Date().toISOString()
            });
            alert('✅ Dato local actualizado.');
          } else {
            await storageService.historicalData.agregar({
              ...datoData,
              estado: 'borrador_local',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            alert('✅ Dato local guardado.');
          }
          navigate('/historicidad');
        }
      } else {
        // Guardar/actualizar en backend (solo para usuarios con permisos)
        if (!canSubmitToBackend) {
          // Usuario registrado sin permisos - guardar localmente
          await storageService.historicalData.agregar({
            ...datoData,
            estado: 'borrador_local',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          alert('✅ Dato guardado localmente. Puedes enviarlo a revisión cuando esté listo.');
          navigate('/historicidad');
        } else {
          // Usuario con permisos - guardar en backend
          if (dato) {
            await datosAPI.actualizar(dato.id, datoData);
            alert('✅ Dato actualizado en el servidor.');
          } else {
            await datosAPI.crear(datoData);
            alert('✅ Dato creado en el servidor.');
          }
          navigate('/historicidad');
        }
      }
    } catch (error) {
      console.error('Error al guardar/enviar dato:', error);
      alert('Error al guardar/enviar el dato. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCatalogoChange = (catalogoId) => {
    setSelectedCatalogos(prev => {
      const exists = prev.some(c => (typeof c === 'object' ? c.id : c) === parseInt(catalogoId));
      if (exists) {
        return prev.filter(c => (typeof c === 'object' ? c.id : c) !== parseInt(catalogoId));
      } else {
        const catalogo = allCatalogos.find(cat => cat.id === parseInt(catalogoId));
        return [...prev, catalogo || parseInt(catalogoId)];
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/historicidad')}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-800 mb-4"
          >
            <FiArrowLeft />
            Volver a Historicidad
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? '✏️ Editar Dato Histórico' : '➕ Nuevo Dato Histórico'}
          </h1>
          <p className="text-gray-600 mt-2">
            {canSubmitToBackend
              ? 'Puedes guardar directamente en el servidor'
              : 'Guarda localmente o envía a revisión para publicación'}
          </p>
        </div>

        {/* Mensaje informativo */}
        {!canSubmitToBackend && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Proceso de publicación:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Guarda tu dato localmente (solo en tu navegador)</li>
                <li>Cuando esté listo, envíalo a revisión</li>
                <li>Un gestor lo revisará y aprobará para publicación</li>
              </ol>
            </div>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Tema */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tema *
            </label>
            <input
              type="text"
              value={tema}
              onChange={(e) => setTema(e.target.value)}
              placeholder="Ej: El Arca de Noé"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción *
            </label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción breve del dato histórico..."
              required
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Texto Histórico */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Texto Histórico *
            </label>
            <ReactQuill
              value={textoHistorico}
              onChange={setTextoHistorico}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  ['link'],
                  ['clean']
                ]
              }}
              placeholder="Análisis histórico detallado con formato enriquecido..."
              className="bg-white rounded-lg"
              style={{ height: '300px', marginBottom: '50px' }}
            />
          </div>

          {/* Grid de campos secundarios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fecha del Evento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha del Evento
              </label>
              <input
                type="text"
                value={fechaEvento}
                onChange={(e) => setFechaEvento(e.target.value)}
                placeholder="Ej: 2500 a.C."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Cita Bíblica */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cita Bíblica
              </label>
              <input
                type="text"
                value={citaBiblica}
                onChange={(e) => setCitaBiblica(e.target.value)}
                placeholder="Ej: Génesis 6-9"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Strong Hebreo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Strong Hebreo
              </label>
              <input
                type="text"
                value={strongHebreo}
                onChange={(e) => setStrongHebreo(e.target.value)}
                placeholder="Ej: H8392"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Strong Griego */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Strong Griego
              </label>
              <input
                type="text"
                value={strongGriego}
                onChange={(e) => setStrongGriego(e.target.value)}
                placeholder="Ej: G2787"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Imagen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL de Imagen
            </label>
            <input
              type="url"
              value={imagen}
              onChange={(e) => setImagen(e.target.value)}
              placeholder="https://ejemplo.com/imagen.jpg"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Referencias */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Referencias
            </label>
            <textarea
              value={referencias}
              onChange={(e) => setReferencias(e.target.value)}
              placeholder="Fuentes y referencias bibliográficas..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Palabras Clave */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Palabras Clave
            </label>
            <input
              type="text"
              value={palabrasClave}
              onChange={(e) => setPalabrasClave(e.target.value)}
              placeholder="diluvio, arca, Noé..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Catálogos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catálogos
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-200 rounded-lg">
              {allCatalogos.map((catalogo) => {
                const isSelected = selectedCatalogos.some(c => 
                  (typeof c === 'object' ? c.id : c) === catalogo.id
                );
                return (
                  <label
                    key={catalogo.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCatalogoChange(catalogo.id)}
                      className="rounded text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{catalogo.nombre}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/historicidad')}
              disabled={loading}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>

            {isLocalDato || !canSubmitToBackend ? (
              <>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <FiSave className="w-5 h-5" />
                  {loading ? 'Guardando...' : 'Guardar Localmente'}
                </button>

                {isLocalDato && (
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FiSend className="w-5 h-5" />
                    {loading ? 'Enviando...' : 'Enviar a Revisión'}
                  </button>
                )}
              </>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FiSave className="w-5 h-5" />
                {loading ? 'Guardando...' : (id ? 'Actualizar' : 'Guardar')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}


